import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { Button } from "../components/common/Button";
import { ShoppingCart, ArrowLeft, Package, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { Spinner } from "../components/common/Spinner";
import styles from "./PaintKitDetailPage.module.css";

interface PaletteImage {
  url: string;
  isDefault: boolean;
}

interface ColorPaletteDetail {
  _id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  images: PaletteImage[];
  inStock: boolean;
}

export function ColorPaletteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [palette, setPalette] = useState<ColorPaletteDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const resolvedImages: string[] = useMemo(() => {
    if (!palette) return [];
    if (palette.images && palette.images.length > 0) {
      const sorted = [...palette.images].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
      return sorted.map((img) =>
        img.url.startsWith("http") ? img.url : `${API_BASE_URL}${img.url}`
      );
    }
    if (palette.imageUrl) {
      const url = palette.imageUrl.startsWith("http")
        ? palette.imageUrl
        : `${API_BASE_URL}${palette.imageUrl}`;
      return [url];
    }
    return [];
  }, [palette]);

  useEffect(() => {
    const fetchPaletteDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/color-palettes/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch color palette details");
        }
        const data = await response.json();
        setPalette(data.palette);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching color palette details:", error);
        setError("Failed to load color palette details. Please try again.");
        setLoading(false);
      }
    };

    if (id) {
      fetchPaletteDetails();
    }
  }, [id]);

  const handleAddToCart = () => {
    if (!palette) return;
    addToCart({
      id: palette._id,
      name: palette.name,
      price: palette.price,
      imageUrl: resolvedImages[0] || palette.imageUrl,
      type: "palette",
    });
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Spinner label="Loading color palette details..." size="lg" fullPage />
      </div>
    );
  }

  if (error || !palette) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>{error || "Color palette not found"}</p>
          <Button onClick={() => navigate("/products/palettes")} variant="primary">
            Back to Color Palettes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detailPage}>
      <div className={styles.container}>
        {/* Back Button */}
        <button onClick={() => navigate("/products/palettes")} className={styles.backButton}>
          <ArrowLeft size={20} />
          Back to All Color Palettes
        </button>

        {/* Main Content */}
        <div className={styles.content}>
          {/* Image Section */}
          <div className={styles.imageSection}>
            <img
              src={resolvedImages[selectedImageIndex] || palette.imageUrl}
              alt={palette.name}
              className={styles.mainImage}
            />
            {!palette.inStock && <div className={styles.outOfStock}>Out of Stock</div>}

            {resolvedImages.length > 1 && (
              <>
                <button
                  className={`${styles.navArrow} ${styles.navArrowLeft}`}
                  onClick={() => setSelectedImageIndex((prev) => prev === 0 ? resolvedImages.length - 1 : prev - 1)}
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  className={`${styles.navArrow} ${styles.navArrowRight}`}
                  onClick={() => setSelectedImageIndex((prev) => prev === resolvedImages.length - 1 ? 0 : prev + 1)}
                >
                  <ChevronRight size={24} />
                </button>

                <div className={styles.thumbnailStrip}>
                  {resolvedImages.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      className={`${styles.thumbnail} ${idx === selectedImageIndex ? styles.thumbnailActive : ""}`}
                      onClick={() => setSelectedImageIndex(idx)}
                    >
                      <img src={imgUrl} alt={`${palette.name} ${idx + 1}`} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Details Section */}
          <div className={styles.detailsSection}>
            <div className={styles.header}>
              <h1 className={styles.title}>{palette.name}</h1>
            </div>

            <p className={styles.description}>{palette.description}</p>

            <div className={styles.price}>EGP {palette.price}</div>

            {/* Palette Info */}
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <Package className={styles.infoIcon} size={20} />
                <div>
                  <span className={styles.infoLabel}>Status</span>
                  <span className={styles.infoValue}>
                    {palette.inStock ? "In Stock" : "Out of Stock"}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.actions}>
              <Button
                variant="primary"
                onClick={handleAddToCart}
                disabled={!palette.inStock}
                className={styles.addToCartButton}
              >
                {addedToCart ? (
                  <>
                    <Check size={20} />
                    Added to Cart!
                  </>
                ) : (
                  <>
                    <ShoppingCart size={20} />
                    {palette.inStock ? "Add to Cart" : "Out of Stock"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
